import { Ionicons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { userApi, zoneApi } from '../services/api';
import logger from '../utils/logger';
import { canManageZonePermissions } from '../utils/permissions';

interface ZonePermission {
  id: string;
  zoneId: string;
  userId: string;
  permissionLevel: 'view_only' | 'view_edit' | 'admin';
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  grantedAt?: string;
  createdAt: string;
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

interface Props {
  route: { params: { zoneId: string; zoneName: string } };
  navigation: any;
}

export default function ZonePermissionsScreen({ route, navigation }: Props) {
  // P2: Validate route params
  if (!route?.params?.zoneId) {
    Alert.alert('Error', 'Zone information is missing', [
      { text: 'OK', onPress: () => navigation.goBack() },
    ]);
    return <View style={{ flex: 1 }} />;
  }

  const { zoneId, zoneName } = route.params;
  const { theme } = useTheme();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const hasPermission = user?.roles ? canManageZonePermissions(user.roles) : false;

  const [permissions, setPermissions] = useState<ZonePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedPermissionLevel, setSelectedPermissionLevel] = useState<
    'view_only' | 'view_edit' | 'admin'
  >('view_only');

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? `صلاحيات القسم: ${zoneName}` : `Zone Permissions: ${zoneName}`,
      headerTintColor: '#ffffff',
      headerTitleStyle: {
        color: '#ffffff',
        fontWeight: '700',
      },
    });

    if (!hasPermission) {
      Alert.alert(
        isArabic ? 'صلاحية مطلوبة' : 'Permission Required',
        isArabic
          ? 'لا تملك صلاحية للوصول لهذه الصفحة'
          : 'You do not have permission to access this page.',
        [{ text: isArabic ? 'حسناً' : 'OK', onPress: () => navigation.goBack() }],
      );
      return;
    }

    loadPermissions();
  }, [hasPermission]);

  // تحميل المستخدمين بعد تحميل الصلاحيات لفلترة المستخدمين الموجودين
  useEffect(() => {
    if (!loading) {
      loadUsers();
    }
  }, [permissions, loading]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const data = await zoneApi.getZonePermissions(zoneId);
      setPermissions(data);
    } catch (error) {
      logger.error('Failed to load zone permissions:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'فشل تحميل الصلاحيات' : 'Failed to load permissions',
      );
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const users = await userApi.getUsers();

      // التحقق من أن users موجود وهو array
      if (!users || !Array.isArray(users)) {
        logger.warn('Users response is not an array:', users);
        setAvailableUsers([]);
        return;
      }

      // فلترة المستخدمين الذين لديهم صلاحية بالفعل أو المستخدم الحالي
      const existingUserIds = permissions.map((p) => p.userId);
      const available = users.filter(
        (u: User) => u && u.id && !existingUserIds.includes(u.id) && u.id !== user?.id,
      );

      setAvailableUsers(available);

      if (available.length === 0) {
        logger.info('No available users to add permissions');
      }
    } catch (error: any) {
      logger.error('Failed to load users:', error);
      setAvailableUsers([]); // تعيين array فارغ بدلاً من ترك undefined

      // عرض رسالة خطأ مفصلة
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        (isArabic ? 'فشل تحميل المستخدمين' : 'Failed to load users');

      Alert.alert(isArabic ? 'خطأ في تحميل المستخدمين' : 'Error Loading Users', errorMessage, [
        { text: isArabic ? 'حسناً' : 'OK' },
      ]);
    }
  };

  const handleAddPermission = async () => {
    if (!selectedUser) {
      Alert.alert(
        isArabic ? 'تنبيه' : 'Warning',
        isArabic ? 'يرجى اختيار مستخدم' : 'Please select a user',
      );
      return;
    }

    // التحقق من أن المستخدم المختار موجود في القائمة المتاحة
    const userExists = availableUsers.find((u) => u.id === selectedUser);
    if (!userExists) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'المستخدم المختار غير صالح' : 'Selected user is invalid',
      );
      return;
    }

    try {
      await zoneApi.addZonePermission(zoneId, {
        userId: selectedUser,
        permissionLevel: selectedPermissionLevel,
      });
      Alert.alert(
        isArabic ? 'نجح' : 'Success',
        isArabic ? 'تم إضافة الصلاحية بنجاح' : 'Permission added successfully',
      );
      setModalVisible(false);
      setSelectedUser('');
      setSelectedPermissionLevel('view_only'); // إعادة تعيين القيمة الافتراضية
      loadPermissions();
    } catch (error: any) {
      logger.error('Failed to add permission:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        (isArabic ? 'فشل إضافة الصلاحية' : 'Failed to add permission');
      Alert.alert(isArabic ? 'خطأ' : 'Error', errorMessage);
    }
  };

  const handleRemovePermission = (permissionId: string, userEmail: string) => {
    Alert.alert(
      isArabic ? 'تأكيد الحذف' : 'Confirm Delete',
      isArabic
        ? `هل تريد إزالة صلاحية المستخدم ${userEmail}؟`
        : `Remove permission for ${userEmail}?`,
      [
        { text: isArabic ? 'إلغاء' : 'Cancel', style: 'cancel' },
        {
          text: isArabic ? 'حذف' : 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await zoneApi.removeZonePermission(zoneId, permissionId);
              Alert.alert(
                isArabic ? 'نجح' : 'Success',
                isArabic ? 'تم إزالة الصلاحية بنجاح' : 'Permission removed successfully',
              );
              loadPermissions();
            } catch (error) {
              logger.error('Failed to remove permission:', error);
              Alert.alert(
                isArabic ? 'خطأ' : 'Error',
                isArabic ? 'فشل إزالة الصلاحية' : 'Failed to remove permission',
              );
            }
          },
        },
      ],
    );
  };

  const getPermissionLevelLabel = (level: string) => {
    const labels = {
      view_only: isArabic ? 'عرض فقط' : 'View Only',
      view_edit: isArabic ? 'عرض وتعديل' : 'View & Edit',
      admin: isArabic ? 'مدير' : 'Admin',
    };
    return labels[level as keyof typeof labels] || level;
  };

  const getPermissionLevelColor = (level: string) => {
    const colors = {
      view_only: theme.colors.info,
      view_edit: theme.colors.warning,
      admin: theme.colors.error,
    };
    return colors[level as keyof typeof colors] || theme.colors.textSecondary;
  };

  const renderPermission = ({ item }: { item: ZonePermission }) => {
    const userName = item.user
      ? `${item.user.firstName || ''} ${item.user.lastName || ''}`.trim() || item.user.email
      : item.userId;

    return (
      <View
        style={[
          styles.permissionCard,
          { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
        ]}
      >
        <View style={styles.permissionInfo}>
          <View style={styles.userInfo}>
            <Ionicons name="person-circle" size={40} color={theme.colors.primary} />
            <View style={styles.userDetails}>
              <Text style={[styles.userName, { color: theme.colors.text }]}>{userName}</Text>
              {item.user?.email && (
                <Text style={[styles.userEmail, { color: theme.colors.textSecondary }]}>
                  {item.user.email}
                </Text>
              )}
            </View>
          </View>
          <View
            style={[
              styles.permissionBadge,
              { backgroundColor: getPermissionLevelColor(item.permissionLevel) + '20' },
            ]}
          >
            <Text
              style={[
                styles.permissionLevel,
                { color: getPermissionLevelColor(item.permissionLevel) },
              ]}
            >
              {getPermissionLevelLabel(item.permissionLevel)}
            </Text>
          </View>
        </View>
        <TouchableOpacity
          style={[styles.removeButton, { backgroundColor: theme.colors.error + '20' }]}
          onPress={() => handleRemovePermission(item.id, item.user?.email || userName)}
        >
          <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
          <Text style={[styles.removeButtonText, { color: theme.colors.error }]}>
            {isArabic ? 'إزالة' : 'Remove'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={[styles.centerContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.loadingText, { color: theme.colors.textSecondary, marginTop: 16 }]}>
          {isArabic ? 'جاري تحميل الصلاحيات...' : 'Loading permissions...'}
        </Text>
      </View>
    );
  }

  const handleOpenAddModal = () => {
    // إعادة تحميل المستخدمين المتاحين عند فتح الـ Modal
    if (availableUsers.length === 0) {
      loadUsers();
    }
    setModalVisible(true);
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
          {isArabic ? 'المستخدمون المصرح لهم' : 'Authorized Users'}
        </Text>
        <TouchableOpacity
          style={[styles.addButton, { backgroundColor: theme.colors.primary }]}
          onPress={handleOpenAddModal}
        >
          <Ionicons name="add" size={24} color="#fff" />
          <Text style={styles.addButtonText}>{isArabic ? 'إضافة مستخدم' : 'Add User'}</Text>
        </TouchableOpacity>
      </View>

      {permissions.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="people-outline" size={80} color={theme.colors.textSecondary} />
          <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
            {isArabic ? 'لا توجد صلاحيات مضافة' : 'No permissions added yet'}
          </Text>
        </View>
      ) : (
        <FlatList
          data={permissions}
          renderItem={renderPermission}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.list}
        />
      )}

      {/* Add Permission Modal */}
      <Modal
        visible={modalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'إضافة مستخدم' : 'Add User'}
              </Text>
              <TouchableOpacity onPress={() => setModalVisible(false)}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {isArabic ? 'اختر مستخدم' : 'Select User'}
              </Text>

              {availableUsers.length === 0 ? (
                <View
                  style={[
                    styles.noUsersContainer,
                    { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
                  ]}
                >
                  <Ionicons name="person-outline" size={32} color={theme.colors.textSecondary} />
                  <Text style={[styles.noUsersText, { color: theme.colors.textSecondary }]}>
                    {isArabic ? 'لا يوجد مستخدمين متاحين' : 'No available users'}
                  </Text>
                  <Text style={[styles.noUsersSubtext, { color: theme.colors.textSecondary }]}>
                    {isArabic
                      ? 'جميع المستخدمين لديهم صلاحيات بالفعل'
                      : 'All users already have permissions'}
                  </Text>
                </View>
              ) : (
                <View
                  style={[
                    styles.pickerContainer,
                    { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
                  ]}
                >
                  <Picker
                    selectedValue={selectedUser}
                    onValueChange={(itemValue) => setSelectedUser(itemValue)}
                    style={{ color: theme.colors.text }}
                    dropdownIconColor={theme.colors.text}
                    itemStyle={{ color: theme.colors.text }}
                  >
                    <Picker.Item
                      label={isArabic ? 'اختر مستخدم...' : 'Select a user...'}
                      value=""
                      color={theme.colors.text}
                    />
                    {availableUsers.map((user) => (
                      <Picker.Item
                        key={user.id}
                        label={`${user.firstName || ''} ${user.lastName || ''} (${
                          user.email
                        })`.trim()}
                        value={user.id}
                        color={theme.colors.text}
                      />
                    ))}
                  </Picker>
                </View>
              )}

              <Text style={[styles.label, { color: theme.colors.text }]}>
                {isArabic ? 'مستوى الصلاحية' : 'Permission Level'}
              </Text>

              {['view_only', 'view_edit', 'admin'].map((level) => (
                <TouchableOpacity
                  key={level}
                  style={[
                    styles.permissionOption,
                    {
                      backgroundColor:
                        selectedPermissionLevel === level
                          ? theme.colors.primary + '20'
                          : theme.colors.background,
                      borderColor:
                        selectedPermissionLevel === level
                          ? theme.colors.primary
                          : theme.colors.border,
                    },
                  ]}
                  onPress={() => setSelectedPermissionLevel(level as any)}
                >
                  <Ionicons
                    name={
                      selectedPermissionLevel === level ? 'radio-button-on' : 'radio-button-off'
                    }
                    size={24}
                    color={
                      selectedPermissionLevel === level
                        ? theme.colors.primary
                        : theme.colors.textSecondary
                    }
                  />
                  <Text style={[styles.permissionOptionText, { color: theme.colors.text }]}>
                    {getPermissionLevelLabel(level)}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  styles.cancelButton,
                  { backgroundColor: theme.colors.border },
                ]}
                onPress={() => setModalVisible(false)}
              >
                <Text style={[styles.modalButtonText, { color: theme.colors.text }]}>
                  {isArabic ? 'إلغاء' : 'Cancel'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  styles.confirmButton,
                  { backgroundColor: theme.colors.primary },
                ]}
                onPress={handleAddPermission}
              >
                <Text style={[styles.modalButtonText, { color: '#fff' }]}>
                  {isArabic ? 'إضافة' : 'Add'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },
  header: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
  },
  addButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  list: {
    padding: 16,
  },
  permissionCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  permissionInfo: {
    marginBottom: 12,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  userDetails: {
    marginLeft: 12,
    flex: 1,
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
  },
  userEmail: {
    fontSize: 14,
    marginTop: 4,
  },
  permissionBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  permissionLevel: {
    fontSize: 14,
    fontWeight: '600',
  },
  removeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
    borderRadius: 8,
  },
  removeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 16,
    marginTop: 16,
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  modalBody: {
    padding: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
    marginTop: 16,
  },
  pickerContainer: {
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  noUsersContainer: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noUsersText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 12,
    textAlign: 'center',
  },
  noUsersSubtext: {
    fontSize: 14,
    marginTop: 4,
    textAlign: 'center',
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  permissionOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 8,
  },
  permissionOptionText: {
    fontSize: 16,
    marginLeft: 12,
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  modalButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    marginRight: 8,
  },
  confirmButton: {
    marginLeft: 8,
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});
